import React, { FC, useState } from 'react';
import { ApolloError } from '@apollo/client';
import { find } from 'lodash';
import { Appearance, Button } from '@src/components/Button';
import CloseSvg from '@src/svgs/fr-close.svg';
import { trackEvent } from '@src/metrics';
import { useAppDispatch, useAppSelector } from '@src/redux';
import { closeModal } from '@src/redux/actions/modal';
import {
  useCustomerForActivateSubscriptionQuery,
  useActivateSubscriptionMutation,
  useSubscriptionAndCurrenciesQueryQuery,
  CustomerSubscriptionCycleEnum,
  PaymentMethodTypeEnum,
} from '@src/graphql/generated';
import { setErrorFlashMessage, setSuccessFlashMessage } from '@src/redux/actions/flashMessages';
import { CircularLoading, Size } from '@src/components/CircularLoading';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { fetchNotice } from '@src/redux/actions/notice';
import { getRecruiter } from '@src/redux/selectors/recruiter';
import { SUBSCRIBE_ERROR_MESSAGES, CYCLES, STATIC_MEDIA_DOMAIN } from '@src/constants';
import { Image } from '@src/components/Image';
import { fetchCustomerStatus } from '@src/redux/actions/recruiter';
import { SubscriptionPlans } from './SubscriptionPlans';
import { CreditCards } from './CreditCards';
import { ActivateSubscriptionModalNewProps } from './props';
import './styles.scss';

const IMAGE_SRC = `${STATIC_MEDIA_DOMAIN}/images/logos.png`;

export const ActivateSubscriptionModalNew: FC<ActivateSubscriptionModalNewProps> = ({ onCompleted }) => {
  const dispatch = useAppDispatch();

  const [isActivateSubscriptionLoading, setIsActivateSubscriptionLoading] = useState<boolean>(false);
  const [isInvoiceFormVisible, setIsInvoiceFormVisible] = useState<boolean>(false);
  const [isSubscriptionPlansView, setIsSubscriptionPlansView] = useState<boolean>(true);
  const [selectedPlan, setSelectedPlan] = useState<string | undefined>();

  const recruiter = useAppSelector(getRecruiter);

  const {
    loading: isSubScriptionAndCurrenciesLoading,
    error: isSubScriptionAndCurrenciesError,
    data: subscriptionAndCurrenciesData,
  } = useSubscriptionAndCurrenciesQueryQuery({
    onError: () => setErrorFlashMessage(),
  });
  const {
    loading: isLoading,
    error,
    data,
  } = useCustomerForActivateSubscriptionQuery({
    fetchPolicy: 'network-only',
    onError: () => dispatch(setErrorFlashMessage()),
  });
  const [activateSubscription] = useActivateSubscriptionMutation();

  if (isLoading || isSubScriptionAndCurrenciesLoading) {
    return (
      <div className="activate-subscription-modal">
        <CircularLoading className="activate-subscription-modal__loading" size={Size.LARGE} />
      </div>
    );
  }

  if (error || isSubScriptionAndCurrenciesError) {
    return null;
  }

  const subscriptionPlans = subscriptionAndCurrenciesData?.subscriptionPlans;
  const availableCurrencies = subscriptionAndCurrenciesData?.availableCurrencies;

  const { planType, variants } = selectedPlan
    ? find(subscriptionPlans, subscription => subscription.planType === selectedPlan)
    : find(subscriptionPlans, 'isAvailable');

  const { currency } = find(variants, ({ cycle }) => cycle === CustomerSubscriptionCycleEnum.MONTHLY);

  const activeMonthlyVariant = variants.find(({ cycle }) => {
    return cycle === CYCLES.MONTHLY;
  });

  const { symbol } = find(availableCurrencies, { code: currency.toUpperCase() });

  async function onPaymentMethodAdded(methodType: PaymentMethodTypeEnum): Promise<void> {
    trackEvent({
      context: MetricContext.ACTIVATE_SUBSCRIPTION,
      interaction: MetricAction.SUBSCRIBE,
      payload: { 'Plan Type': selectedPlan || planType },
    });

    setIsActivateSubscriptionLoading(true);

    try {
      await activateSubscription({
        variables: {
          planType: selectedPlan || planType,
          subscriptionCycle: CustomerSubscriptionCycleEnum.MONTHLY,
        },
      });

      await dispatch(fetchCustomerStatus());

      // fetch notice again to hide top bar
      await dispatch(fetchNotice());

      dispatch(setSuccessFlashMessage('Subscription has successfully activated'));

      setIsActivateSubscriptionLoading(false);

      if (methodType === PaymentMethodTypeEnum.INVOICE) {
        dispatch(closeModal());
        onCompleted && onCompleted();
      } else {
        setIsInvoiceFormVisible(true);
      }
    } catch (error) {
      setIsActivateSubscriptionLoading(false);

      if (error instanceof ApolloError && error.graphQLErrors) {
        error.graphQLErrors.forEach(graphQLError => {
          const code: keyof typeof SUBSCRIBE_ERROR_MESSAGES = graphQLError?.extensions?.exception.code;

          if (code) {
            dispatch(setErrorFlashMessage(SUBSCRIBE_ERROR_MESSAGES[code]));
          }
        });
      }

      throw error; // throw error, not to clear card values if mutation returns error code
    }
  }

  const customer = data?.customer;
  const state = customer?.planInfo?.state;

  const hasActiveCard =
    !!(customer?.paymentMethods || []).length || !!(customer?.paysByInvoice && customer?.isPayByInvoiceEnabled);

  const onConfirmSubscription = (): void => {
    if (hasActiveCard) {
      if (customer?.paysByInvoice && customer?.isPayByInvoiceEnabled) {
        onPaymentMethodAdded(PaymentMethodTypeEnum.INVOICE);
      } else {
        onPaymentMethodAdded(PaymentMethodTypeEnum.CREDITCARD);
      }
    } else {
      setIsSubscriptionPlansView(false);
    }
  };

  return (
    <div className="activate-subscription-modal-new">
      {isSubscriptionPlansView ? (
        <SubscriptionPlans
          activePlan={selectedPlan || planType}
          customerPlanState={state}
          customerPlanType={customer?.planType || undefined}
          hasActiveCard={hasActiveCard}
          isActivateSubscriptionLoading={isActivateSubscriptionLoading}
          onConfirmSubscription={onConfirmSubscription}
          onPlanSelect={plan => setSelectedPlan(plan)}
          recruiterName={recruiter.firstName}
          subscriptionPlans={subscriptionPlans}
          symbol={symbol}
        />
      ) : (
        <CreditCards
          closeModal={() => dispatch(closeModal())}
          isInvoiceFormVisible={isInvoiceFormVisible}
          isPayByInvoiceEnabled={!!customer?.isPayByInvoiceEnabled}
          onChangePlan={() => setIsSubscriptionPlansView(true)}
          onCompleted={onCompleted}
          onPaymentMethodAdded={onPaymentMethodAdded}
          recruiterName={recruiter.firstName}
          selectedPlanPrice={activeMonthlyVariant.price}
          symbol={symbol}
        />
      )}

      <Button
        appearance={Appearance.SECONDARY}
        className="activate-subscription-modal-new__close"
        icon={CloseSvg}
        onClick={() => dispatch(closeModal())}
      />

      <div className="activate-subscription-modal-new__company-logos-wrapper">
        <Image className="activate-subscription-modal-new__logos" src={IMAGE_SRC} />
      </div>
    </div>
  );
};
